import React, { useState } from 'react';
import { Archive, ChevronDown, ChevronUp, ClipboardList, Download, Link2, ShieldCheck, Share2, Trash2, Upload, UploadCloud } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
type ItalyStatus = 'missing' | 'needs-review' | 'uploaded';
type Citation = {
  text: string;
  url?: string;
};
type ItalyRequirement = {
  name: string;
  status: ItalyStatus;
  provided: string;
  why: string;
  note: string;
  citations: Citation[];
  filename?: string;
  uploadedOn?: string;
};
type ItalyCategory = {
  id: string;
  title: string;
  missing: number;
  icon: typeof ShieldCheck;
  documents: ItalyRequirement[];
};
const categories: ItalyCategory[] = [{
  id: 'required',
  title: 'Required',
  missing: 5,
  icon: ShieldCheck,
  documents: [{
    name: 'Product formulation',
    status: 'uploaded',
    filename: '7559 Veno Complex Capsules - ohne Weißdorn - V3',
    uploadedOn: '17 Aug 2026',
    provided: 'The full quantitative formulation as manufactured — every active and excipient with its quantity per capsule.',
    why: 'The formulation is the basis for every ingredient verdict and for the quantitative declaration on the label.',
    note: 'Processed — but three ingredients fail as formulated: vitamin E above the Italian maximum daily level, the Hamamelis species and plant part unresolved, and horse chestnut above the accepted aescin dose. See Formula.',
    citations: []
  }, {
    name: 'Final commercial label (Italian-language, PDF)',
    status: 'missing',
    provided: 'The finished Italian-language label artwork, matching exactly what will be printed and sold.',
    why: 'Submitted together with the Italian notification — must match the finished product exactly.',
    note: 'Not finalised — depends on Formula and Claims resolution first.',
    citations: [{
      text: 'Ministero della Salute',
      url: 'https://www.salute.gov.it/portale/temi/p2_6.jsp?lingua=italiano&id=1267&area=Alimenti%20particolari%20e%20integratori&menu=integratori'
    }, {
      text: 'D.Lgs. 169/2004',
      url: 'https://www.gazzettaufficiale.it/eli/id/2004/07/15/004G0201/sg'
    }]
  }, {
    name: 'Full Italian commercial label compliance review',
    status: 'missing',
    provided: 'An independent conformity review of the finalised Italian-language label — mandatory particulars, claims wording, warnings, allergen declarations, and mandatory titration statements.',
    why: 'Required under Regulation (EU) No 1169/2011 and D.Lgs. 169/2004.',
    note: 'To be performed once the label is drafted.',
    citations: []
  }, {
    name: 'Complete quantitative ingredient declaration (analytically-verified)',
    status: 'missing',
    provided: 'Lab-tested average analytical values for every declared ingredient, corresponding to one single daily dose.',
    why: 'Article 6(6) requires declared quantities to match one labelled daily dose; Article 8 requires those values to be analytically verified, not theoretical figures recalculated from the formula on paper.',
    note: 'Not provided — analytical testing has not been supplied for any declared ingredient.',
    citations: [{
      text: 'D.Lgs. 169/2004, Art. 6(6) & 8',
      url: 'https://www.gazzettaufficiale.it/eli/id/2004/07/15/004G0201/sg'
    }]
  }, {
    name: 'Novel Food technical file for nattokinase',
    status: 'missing',
    provided: 'Supplier specification, Certificate of Analysis (CoA), and confirmation that the nattiase® raw material matches the authorised Novel Food specification and conditions of use.',
    why: "Nattokinase is regulated under the EU Novel Food framework — without this file the ingredient can't be confirmed as compliant.",
    note: 'Outstanding — see Formula tab.',
    citations: [{
      text: 'Commission Implementing Decision (EU) 2017/115',
      url: 'https://eur-lex.europa.eu/eli/dec_impl/2017/115/oj/eng'
    }, {
      text: 'Regulation (EU) 2017/2470',
      url: 'https://eur-lex.europa.eu/eli/reg_impl/2017/2470/oj/eng'
    }]
  }, {
    name: 'Supplier documentation bundle',
    status: 'missing',
    provided: 'Written confirmation from raw-material suppliers of: Latin binomial and plant part for the bitter orange, apple, grape seed, sweet clover and olive extracts; Citrus aurantium variety (bitter vs. sweet); cypress twig-sourcing; horse chestnut dose/standardisation; and the actual Hamamelis species and part used.',
    why: "Underlies eight separate Formula findings — three of them (the Hamamelis species/part, horse chestnut's aescin dose, and vitamin E's dose) are the review's three live compliance risks and should be escalated ahead of the rest.",
    note: 'Outstanding — requested from suppliers.',
    citations: [{
      text: 'DM 10 agosto 2018, Allegato 1',
      url: 'https://www.gazzettaufficiale.it/eli/id/2018/09/26/18A06095/SG'
    }]
  }]
}, {
  id: 'recommended',
  title: 'Recommended',
  missing: 3,
  icon: ClipboardList,
  documents: [{
    name: 'Safety/toxicological justification file',
    status: 'missing',
    provided: 'A compiled safety and toxicological justification covering cumulative coumarin exposure from the sweet clover extract.',
    why: "Supports the product's overall safety file.",
    note: 'Not yet compiled.',
    citations: [{
      text: 'Regulation (EC) No 178/2002, Art. 14 & 17',
      url: 'https://eur-lex.europa.eu/eli/reg/2002/178/oj/eng'
    }]
  }, {
    name: 'Claims substantiation file',
    status: 'missing',
    provided: "Substantiation file for any claims beyond the 'ready to use' set — either pre-2008 application evidence or Italy's Allegato 1 reference wording.",
    why: "'Ready to use' claims are self-substantiating under Regulation (EU) No 432/2012; anything else needs its own evidence file.",
    note: 'Depends on final claims selection.',
    citations: [{
      text: 'Regulation (EC) No 1924/2006, Art. 6 & 28',
      url: 'https://eur-lex.europa.eu/eli/reg/2006/1924/oj/eng'
    }, {
      text: 'Regulation (EU) No 432/2012',
      url: 'https://eur-lex.europa.eu/eli/reg/2012/432/oj/eng'
    }]
  }, {
    name: 'Manufacturing/GMP compliance documentation',
    status: 'missing',
    provided: 'Standard HACCP-based food-safety management evidence from the contract manufacturer.',
    why: 'Typically requested if the Ministry queries during review.',
    note: "Not reviewed — outside this review's scope.",
    citations: [{
      text: 'Regulation (EC) No 852/2004',
      url: 'https://eur-lex.europa.eu/eli/reg/2004/852/oj/eng'
    }]
  }]
}];
interface ItalyDossierTabProps {
  onScanLabel: () => void;
}
export function ItalyDossierTab({
  onScanLabel
}: ItalyDossierTabProps) {
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({
    required: true,
    recommended: true
  });
  return <div className="space-y-6" data-id="element-725" data-mp="2ohscqdhn5cov">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-726" data-mp="2oiu09hfufrnh">
        <div data-id="element-727" data-mp="2jqtsx3xg1wgv">
          <h2 className="font-[Figtree] text-2xl font-semibold tracking-tight text-evergreen" data-id="element-728" data-mp="2jq0sekp2w376">Veno Complex Capsules</h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[#6d7e71]" data-id="element-729" data-mp="2yeeyd4hsk7ry">Baseline compliance dossier for Italy — upload documents to build it.</p>
        </div>
        <button type="button" onClick={onScanLabel} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-lime-glow px-4 py-3 text-sm font-semibold text-[#182d22] shadow-sm transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-forest-green focus:ring-offset-2" data-id="element-730" data-mp="37gxyvhy3u8b1">
          <UploadCloud className="h-4 w-4" data-id="element-731" data-mp="1934oqcls111w" />
          Scan a label
        </button>
      </div>

      <section className="max-w-2xl rounded-2xl border-2 border-forest-green bg-[#f2fbf4] p-5 shadow-sm" aria-label="Selected market" data-id="element-732" data-mp="1xjvtku3ivgnb">
        <div className="flex items-start justify-between gap-4" data-id="element-733" data-mp="27c1ghpex37vn">
          <div data-id="element-734" data-mp="27dw4xdpedi4h">
            <p className="flex items-center gap-2 font-[Figtree] text-lg font-semibold text-forest-green" data-id="element-735" data-mp="2bddbkagk8lb8">
              <span className="text-xl" aria-hidden="true" data-id="element-736" data-mp="1ends3kyvg2dv">
                🇮🇹
              </span>
              Italy
            </p>
            <p className="mt-3 text-sm text-[#5f7567]" data-id="element-737" data-mp="1jn063wmixmnq">Food Supplement (Integratore Alimentare) — provisional</p>
          </div>
          <span className="rounded-full bg-amber-100 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-800" data-id="element-738" data-mp="1nmhfh152n3y6">Medium risk</span>
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#e1ece0]" aria-label="11% complete" data-id="element-739" data-mp="2z260vo2ofuwv">
          <div className="h-full w-[11%] rounded-full bg-forest-green" data-id="element-740" data-mp="340h9jvh8jzdi" />
        </div>
        <div className="mt-3 flex items-center justify-between font-mono text-xs font-semibold" data-id="element-741" data-mp="3e4xrqvi6tskh">
          <span className="text-[#6b7d70]" data-id="element-742" data-mp="397xjcdxv8dq7">Required 1/9</span>
          <span className="text-red-700" data-id="element-743" data-mp="34fxo2ysqq8zy">8 missing</span>
        </div>
      </section>

      <section aria-labelledby="italy-required-heading" data-id="element-744" data-mp="34dtb7rcv5znj">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-745" data-mp="30dsxk8xl9shj">
          <h2 id="italy-required-heading" className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-746" data-mp="2vgali9o9yx5b">
            Required documents · Italy
          </h2>
          <button type="button" className="inline-flex items-center justify-center gap-2 self-start rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-800 transition hover:bg-red-50 sm:self-auto" data-id="element-747" data-mp="2lh1u9oksnwmn">
            <Trash2 className="h-4 w-4" data-id="element-748" data-mp="jp5hycqsyiqt" />
            Delete market dossier
          </button>
        </div>

        <div className="mt-4 rounded-2xl border border-[#dce3da] bg-white p-6 shadow-sm" data-id="element-749" data-mp="fm8zqn56cone">
          <div className="flex items-center justify-between gap-4" data-id="element-750" data-mp="1moa3iaotq3mf">
            <span className="flex items-center gap-2 font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-751" data-mp="1ijtyebn1b089">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#eff3ed] text-[#6e8173]" data-id="element-752" data-mp="1wj1xz5n40acz">
                <Archive className="h-5 w-5" data-id="element-753" data-mp="1rm35ssn0adhf" />
              </span>
              Documents
            </span>
            <span className="font-mono text-sm font-semibold text-[#6d7e71]" data-id="element-754" data-mp="13tms0uaax7nx">Uploaded 1/9</span>
          </div>
          <div className="mt-5 h-2.5 overflow-hidden rounded-full bg-[#edf1eb]" aria-label="11% documents complete" data-id="element-755" data-mp="yurzv1qg26r5">
            <div className="h-full w-[11%] rounded-full bg-[#76867c]" data-id="element-756" data-mp="18v2acel3h02m" />
          </div>
          <div className="mt-5 grid gap-6 md:grid-cols-2" data-id="element-757" data-mp="l57xvy4otdiw">
            <div className="space-y-2 font-mono text-sm text-[#607266]" data-id="element-758" data-mp="l3n5of5ui6o7">
              <StatLine label="Uploaded" value="1" data-id="element-759" data-mp="g53hs97oykt7" />
              <StatLine label="Expiring soon" value="0" data-id="element-760" data-mp="g4ah9pzbsrji" />
              <StatLine label="Needs review" value="0" data-id="element-761" data-mp="2rsoucazcfhpr" />
            </div>
            <div className="space-y-2 font-mono text-sm text-[#607266]" data-id="element-762" data-mp="2wkq9q5rt5ok0">
              <StatLine label="Missing" value="8" dot="bg-red-600" data-id="element-763" data-mp="2isscas7glh3o" />
              <StatLine label="Expired" value="0" data-id="element-764" data-mp="2iumvo233115n" />
              <StatLine label="Incomplete" value="0" data-id="element-765" data-mp="2mtvqla9uz52s" />
            </div>
          </div>
          <p className="mt-5 font-mono text-xs font-semibold text-[#718174]" data-id="element-766" data-mp="3ak7vsddpynpo">11% complete</p>
        </div>
      </section>

      <section aria-labelledby="italy-categories-heading" data-id="element-767" data-mp="3fjay3q2befty">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-768" data-mp="3fgorptr5u6db">
          <h2 id="italy-categories-heading" className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-769" data-mp="31lmeeljzheqh">
            Notification &amp; supporting evidence
          </h2>
          <div className="flex flex-wrap gap-2" data-id="element-770" data-mp="1q2tgmdyrelq1">
            <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-771" data-mp="1v3qwul07l5ow">
              <Share2 className="h-4 w-4" data-id="element-772" data-mp="3rssb5kdtfaye" />
              Share documents
            </button>
            <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-773" data-mp="3n2byi2htjtd3">
              <Link2 className="h-4 w-4" data-id="element-774" data-mp="3e1m1ivgtnwxs" />
              Request documents
            </button>
          </div>
        </div>

        <div className="mt-4 space-y-4" data-id="element-775" data-mp="391rdczofllgf">
          {categories.map(category => <CategoryCard key={category.id} category={category} open={Boolean(openCategories[category.id])} onToggle={() => setOpenCategories(open => ({
          ...open,
          [category.id]: !open[category.id]
        }))} data-id="element-776" data-mp="342xyp3xeroeo" />)}
        </div>
      </section>
    </div>;
}
function StatLine({
  label,
  value,
  dot
}: {
  label: string;
  value: string;
  dot?: string;
}) {
  return <div className="flex items-center justify-between gap-3" data-id="element-777" data-mp="2g7uwlhvcu9vo">
      <span className="inline-flex items-center gap-1.5" data-id="element-778" data-mp="2l6xp62u5enmr">
        {dot && <span className={`h-2 w-2 rounded-full ${dot}`} data-id="element-779" data-mp="2l54kgt33idhe" />}
        {label}
      </span>
      <span className="font-semibold text-evergreen" data-id="element-780" data-mp="2q0aekf6niygk">{value}</span>
    </div>;
}
function CategoryCard({
  category,
  open,
  onToggle
}: {
  category: ItalyCategory;
  open: boolean;
  onToggle: () => void;
}) {
  const Icon = category.icon;
  return <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-781" data-mp="2283cr0i5pz9r">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center gap-3 px-5 py-4 text-left transition hover:bg-[#f8faf7] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest-green" data-id="element-782" data-mp="269ovh2hdh7rv">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-[#6f8174]" data-id="element-783" data-mp="3mnx5jxkyhomm">
          <Icon className="h-5 w-5" data-id="element-784" data-mp="3l8caje9w3snk" />
        </span>
        <span className="min-w-0 flex-1" data-id="element-785" data-mp="3l9464p3hlqdu">
          <span className="block font-[Figtree] text-base font-semibold text-evergreen" data-id="element-786" data-mp="3g9858yq36f2i">{category.title}</span>
          <span className="mt-0.5 block font-mono text-xs text-[#718174]" data-id="element-787" data-mp="3u8qy6kabtju8">{category.missing} missing</span>
        </span>
        {open ? <ChevronUp className="h-5 w-5 text-[#718174]" data-id="element-788" data-mp="3q8gwhjtkhfl6" /> : <ChevronDown className="h-5 w-5 text-[#718174]" data-id="element-789" data-mp="7zzh84op3q2r" />}
      </button>
      {open && <div className="border-t border-[#e5ebe3] px-5" data-id="element-790" data-mp="326anajhcu1y">
          {category.documents.map(document => document.name === 'Product formulation' ? <FormulationResultRow key={document.name} document={document} data-id="element-791" data-mp="hsdlb438x8s3" /> : <RequirementRow key={document.name} document={document} data-id="element-792" data-mp="cttzr4skdz09" />)}
        </div>}
    </section>;
}
function RequirementRow({
  document
}: {
  document: ItalyRequirement;
}) {
  const [showWhy, setShowWhy] = useState(false);
  return <article className="flex flex-col gap-3 border-b border-[#edf0eb] py-5 last:border-b-0 lg:flex-row lg:items-start" data-id="element-793" data-mp="15eh9bzu3tmgc">
      <div className="flex min-w-0 flex-1 gap-3" data-id="element-794" data-mp="1aay8dp2udl87">
        {document.status === 'uploaded' ? <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-forest-green text-white" aria-hidden="true" data-id="element-795" data-mp="wfcbkvqd9x58">
            <ShieldCheck className="h-3 w-3" data-id="element-796" data-mp="mmy6bt02y8eb" />
          </span> : document.status === 'needs-review' ? <span className="mt-1 h-5 w-5 shrink-0 rounded-full border-2 border-amber-500 bg-amber-50" aria-hidden="true" data-id="element-797" data-mp="rf85lzn34kt9" /> : <span className="mt-1 h-5 w-5 shrink-0 rounded-full border-2 border-[#839589] bg-white" aria-hidden="true" data-id="element-798" data-mp="cr3jelotpr5q" />}
        <div className="min-w-0 flex-1" data-id="element-799" data-mp="3oluu0lp4mfk">
          <div className="flex flex-wrap items-center gap-2" data-id="element-800" data-mp="3pncinx7w769">
            <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-801" data-mp="8nfde7fub3ml">{document.name}</h3>
            <StatusChip status={document.status} data-id="element-802" data-mp="2ackxo2wqal82" />
          </div>
          <div className="mt-3 max-w-2xl" data-id="element-803" data-mp="2aexluxt2xrtk">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#819083]" data-id="element-804" data-mp="25lmg70007h38">What to provide</p>
            <p className="mt-1 text-sm leading-relaxed text-[#687a6d]" data-id="element-805" data-mp="20nunw4vg5syi">{document.provided}</p>
          </div>
          <button type="button" onClick={() => setShowWhy(visible => !visible)} aria-expanded={showWhy} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#637667] hover:text-forest-green hover:underline" data-id="element-806" data-mp="1wn0zzbi08e5m">
            <ChevronDown className={`h-3.5 w-3.5 transition ${showWhy ? 'rotate-180' : ''}`} data-id="element-807" data-mp="2k9pi9yaxe8mr" />
            Why this document matters
          </button>
          {showWhy && <p className="mt-2 max-w-2xl rounded-lg bg-[#f4f7f2] p-3 text-sm leading-relaxed text-[#596c5e]" data-id="element-808" data-mp="18wepldkj2hsg">{document.why}</p>}
          <p className={`mt-3 max-w-2xl rounded-lg px-3 py-2 text-xs font-medium leading-relaxed ${document.status === 'needs-review' ? 'border border-amber-200 bg-amber-50 text-amber-900' : document.status === 'uploaded' ? 'border border-[#cfe4d2] bg-[#f2fbf4] text-[#3f5c48]' : 'bg-[#f4f7f2] text-[#596c5e]'}`} data-id="element-809" data-mp="13vr417f2it5m">
            {document.note}
          </p>
          {document.citations.length > 0 && <div className="mt-3 flex flex-wrap gap-2" data-id="element-810" data-mp="24zl5dw7q329">
              {document.citations.map(citation => <RegulationLink key={citation.text} text={citation.text} href={citation.url} data-id="element-811" data-mp="76f5f3qkg5jl" />)}
            </div>}
        </div>
      </div>
      <div className="shrink-0 lg:pl-4" data-id="element-812" data-mp="g3phgrb7jypk">
        <button type="button" className="inline-flex items-center gap-2 rounded-xl bg-evergreen px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-forest-green" data-id="element-813" data-mp="g2o6sny3te8d">
          <Upload className="h-4 w-4" data-id="element-814" data-mp="1z053cm3bt693" />
          Upload
        </button>
      </div>
    </article>;
}
function FormulationResultRow({
  document
}: {
  document: ItalyRequirement;
}) {
  const [showWhy, setShowWhy] = useState(false);
  return <article className="flex flex-col gap-3 border-b border-[#edf0eb] py-5 last:border-b-0 lg:flex-row lg:items-center" data-id="element-815" data-mp="27rdpmyc3wur7">
      <div className="flex min-w-0 flex-1 gap-3" data-id="element-816" data-mp="23rdbzfwu0nl7">
        <span className="mt-1 h-5 w-5 shrink-0 rounded-full border-2 border-amber-500 bg-amber-50" aria-hidden="true" data-id="element-817" data-mp="1g0zgfff1g6lv" />
        <div className="min-w-0" data-id="element-818" data-mp="1b2xwytdpa59v">
          <div className="flex flex-wrap items-center gap-2" data-id="element-819" data-mp="1b40o92exq9qb">
            <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-820" data-mp="1p27d5t1wozdu">{document.name}</h3>
            <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-amber-800" data-id="element-821" data-mp="1k1t6offydxhx">
              Needs review
            </span>
          </div>
          <div className="mt-3 max-w-2xl" data-id="element-822" data-mp="2vhrmr39jtjsn">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#819083]" data-id="element-823" data-mp="33za3wwypcxk4">What to provide</p>
            <p className="mt-1 text-sm leading-relaxed text-[#687a6d]" data-id="element-824" data-mp="381oi33rff952">{document.provided}</p>
          </div>
          <p className="mt-3 font-mono text-xs text-[#718174]" data-id="element-825" data-mp="2u25mtbjprsa6">
            {document.filename} · Uploaded: {document.uploadedOn}
          </p>
          <p className="mt-3 max-w-2xl rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium leading-relaxed text-amber-900" data-id="element-826" data-mp="3mpjdirwtjrlu">
            {document.note}
          </p>
          <button type="button" onClick={() => setShowWhy(visible => !visible)} aria-expanded={showWhy} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#637667] hover:text-forest-green hover:underline" data-id="element-827" data-mp="3rqfh14d9dbqq">
            <ChevronDown className={`h-3.5 w-3.5 transition ${showWhy ? 'rotate-180' : ''}`} data-id="element-828" data-mp="9gwtttmo3fut" />
            Why this document matters
          </button>
          {showWhy && <p className="mt-2 max-w-2xl rounded-lg bg-[#f4f7f2] p-3 text-sm leading-relaxed text-[#596c5e]" data-id="element-829" data-mp="25vvj40m9h29t">{document.why}</p>}
        </div>
      </div>
      <div className="shrink-0 lg:pl-4" data-id="element-830" data-mp="1w50skk1zfgik">
        <div className="flex flex-wrap justify-end gap-2" data-id="element-831" data-mp="1w3y40is4tq5t">
          <RowAction icon={Upload} data-id="element-832" data-mp="2143xm4hypmqj">Replace</RowAction>
          <RowAction icon={Download} data-id="element-833" data-mp="212st22gp0ayd">Download</RowAction>
          <RowAction icon={Archive} data-id="element-834" data-mp="1m734bwk4hjpy">Move</RowAction>
          <RowAction data-id="element-835" data-mp="1m5iak95mu4t5">Document history</RowAction>
          <RowAction icon={Trash2} tone="danger" data-id="element-836" data-mp="1r6fr6hazuqsj">
            Delete
          </RowAction>
        </div>
      </div>
    </article>;
}
function RowAction({
  icon: Icon,
  children,
  tone = 'default'
}: {
  icon?: typeof Upload;
  children: React.ReactNode;
  tone?: 'default' | 'danger';
}) {
  const style = tone === 'danger' ? 'border-red-200 text-red-700 hover:bg-red-50' : 'border-[#d6dfd3] text-evergreen hover:bg-[#f7faf5]';
  return <button type="button" className={`inline-flex items-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-sm font-semibold transition ${style}`} data-id="element-837" data-mp="3cihg0tty59z6">
      {Icon ? <Icon className="h-3.5 w-3.5" data-id="element-838" data-mp="3hp5v03oszumn" /> : <span className="h-3.5 w-3.5" data-id="element-839" data-mp="3hpxqleiehscx" />}
      {children}
    </button>;
}
function StatusChip({
  status
}: {
  status: ItalyStatus;
}) {
  const style = status === 'needs-review' ? 'border-amber-200 bg-amber-50 text-amber-800' : status === 'uploaded' ? 'border-[#cfe4d2] bg-[#f2fbf4] text-forest-green' : 'border-red-200 bg-red-50 text-red-800';
  const label = status === 'needs-review' ? 'Needs review' : status === 'uploaded' ? 'Uploaded' : 'Missing';
  return <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] ${style}`} data-id="element-840" data-mp="i8fz4x9af6m7">{label}</span>;
}